import { useEffect, useState } from "react";
import api, { fmtDate, todayDMY, nzISODate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NumberInput from "@/components/NumberInput";

/**
 * Configuration > Labour factors.
 *
 * Admin-only edits. Managers see the table read-only. Workers never land here.
 * Columns: trade, keyword, unit, hours/unit, source note, last reviewed, warning label,
 * actions (Edit inline, Delete with confirm, Mark reviewed today).
 */
export default function LabourFactorsConfig() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newRow, setNewRow] = useState({ trade: "", keyword: "", unit: "ea", hours_per_unit: 0, source_note: "" });
  const [msg, setMsg] = useState("");

  async function load() {
    const { data } = await api.get("/labour-factors");
    setRows(data || []);
  }
  useEffect(() => { load(); }, []);

  function flash(m) { setMsg(m); setTimeout(() => setMsg(""), 2500); }

  async function add() {
    if (!newRow.trade || !newRow.keyword) { flash("Trade and keyword are required."); return; }
    await api.post("/labour-factors", {
      ...newRow,
      hours_per_unit: parseFloat(newRow.hours_per_unit || 0),
      last_reviewed: todayDMY(),
    });
    setNewRow({ trade: "", keyword: "", unit: "ea", hours_per_unit: 0, source_note: "" });
    await load(); flash("Factor added ✓");
  }
  async function saveEdit() {
    const payload = { ...editing, hours_per_unit: parseFloat(editing.hours_per_unit || 0) };
    await api.patch(`/labour-factors/${editing.id}`, payload);
    setEditing(null); await load(); flash("Saved ✓");
  }
  async function del(r) {
    if (!window.confirm(`Delete labour factor "${r.keyword}" (${r.trade})? Are you sure?`)) return;
    await api.delete(`/labour-factors/${r.id}`);
    await load(); flash("Deleted ✓");
  }
  async function markReviewed(r) {
    const today = nzISODate();
    await api.patch(`/labour-factors/${r.id}`, { last_reviewed: today });
    await load(); flash("Marked reviewed today ✓");
  }

  return (
    <div className="card" data-testid="labour-factors-config">
      {msg && <div className="text-green-400 text-sm mb-2" data-testid="lf-msg">{msg}</div>}
      <p className="text-xs opacity-70 mb-3">
        Hours per unit per trade. Luke reviews these before each new project — the badge "starting value, review before use" is a reminder, not a lock.
      </p>

      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3" data-testid="lf-new-row">
          <input className="input md:col-span-1" placeholder="Trade" value={newRow.trade}
                 onChange={e=>setNewRow({...newRow, trade:e.target.value})} data-testid="lf-new-trade"/>
          <input className="input md:col-span-2" placeholder="Keyword(s) — pipe separated e.g. gib|plasterboard"
                 value={newRow.keyword} onChange={e=>setNewRow({...newRow, keyword:e.target.value})} data-testid="lf-new-keyword"/>
          <input className="input" placeholder="Unit" value={newRow.unit}
                 onChange={e=>setNewRow({...newRow, unit:e.target.value})} data-testid="lf-new-unit"/>
          <NumberInput className="input" step="0.01" placeholder="hrs/unit"
                 value={newRow.hours_per_unit} onChange={e=>setNewRow({...newRow, hours_per_unit:e.target.value})} data-testid="lf-new-hours"/>
          <button className="btn-silver" onClick={add} data-testid="btn-lf-add">+ Add factor</button>
          <input className="input md:col-span-6" placeholder="Source note (where this starting value came from)"
                 value={newRow.source_note} onChange={e=>setNewRow({...newRow, source_note:e.target.value})} data-testid="lf-new-note"/>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="table text-sm" data-testid="lf-table">
          <thead><tr>
            <th>Trade</th><th>Keyword(s)</th><th>Unit</th>
            <th className="text-right">hrs/unit</th><th>Source note</th>
            <th>Last reviewed</th><th>Warning</th><th></th>
          </tr></thead>
          <tbody>
          {rows.map(r => editing?.id === r.id ? (
            <tr key={r.id} data-testid={`lf-edit-${r.id}`}>
              <td><input className="input" value={editing.trade} onChange={e=>setEditing({...editing, trade:e.target.value})} data-testid={`lf-edit-trade-${r.id}`}/></td>
              <td><input className="input" value={editing.keyword} onChange={e=>setEditing({...editing, keyword:e.target.value})}/></td>
              <td><input className="input w-20" value={editing.unit} onChange={e=>setEditing({...editing, unit:e.target.value})}/></td>
              <td><NumberInput className="input text-right w-20" step="0.01" value={editing.hours_per_unit}
                         onChange={e=>setEditing({...editing, hours_per_unit:e.target.value})} data-testid={`lf-edit-hours-${r.id}`}/></td>
              <td><input className="input" value={editing.source_note||""} onChange={e=>setEditing({...editing, source_note:e.target.value})}/></td>
              <td>{(editing.last_reviewed||"").slice(0,10)}</td>
              <td className="text-[10px] opacity-70">{editing.warning_label}</td>
              <td className="flex gap-2 text-xs">
                <button className="hub-link" onClick={saveEdit} data-testid={`btn-lf-save-${r.id}`}>Save</button>
                <button className="opacity-70" onClick={()=>setEditing(null)}>Cancel</button>
              </td>
            </tr>
          ) : (
            <tr key={r.id} data-testid={`lf-row-${r.id}`}>
              <td>{r.trade}</td>
              <td className="text-xs opacity-80">{r.keyword}</td>
              <td>{r.unit}</td>
              <td className="text-right">{r.hours_per_unit}</td>
              <td className="text-xs opacity-80">{r.source_note||"—"}</td>
              <td className="text-xs">{fmtDate(r.last_reviewed)}</td>
              <td className="text-[10px] text-amber-300">{r.warning_label}</td>
              <td className="flex gap-2 text-xs">
                {isAdmin && <>
                  <button className="hub-link" onClick={()=>setEditing({...r})} data-testid={`btn-lf-edit-${r.id}`}>Edit</button>
                  <button className="text-silver" onClick={()=>markReviewed(r)} data-testid={`btn-lf-review-${r.id}`}>Mark reviewed today</button>
                  <button className="text-red-400" onClick={()=>del(r)} data-testid={`btn-lf-del-${r.id}`}>Delete</button>
                </>}
                {!isAdmin && <span className="opacity-50">read-only</span>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan="8" className="opacity-50 text-center py-3">No labour factors yet.</td></tr>
          )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
